import React, { useContext, useState, useEffect, ReactNode } from "react";
import { toast } from "react-hot-toast";
import axios from "axios";
import { 
  IProduct, ICoupon, IPayment, ICreateProductBody, 
  IAnalyticsData, IDailySalesData
} from "../utils/interface";
import { IOrder } from "../utils/interface";

import { jwtDecode } from "jwt-decode";
import axiosInstance from "../utils/axiosInstance"; // Adjust the path accordingly

const BASE_URL = "http://localhost:5000/api";

interface User { 
  _id: string;
  name: string;
  role?: string;
  token?: string;
  email?: string;
  isAdmin: boolean;
}

interface IUpdateProductBody extends Partial<ICreateProductBody> {
  _id: string;
}

// export interface IOrder {
//   _id: string;
//   user: string | { _id: string; name: string };
//   userName?: string;
//   orderItems: {
//     name: string;
//     quantity: number;
//     price: number;
//     image: string;
//     product: string;
//   }[];
//   totalAmount: number;
//   paymentStatus: "pending" | "approved" | "declined";
//   orderStatus: "pending" | "processing" | "completed" | "cancelled";
//   createdAt: string;
//   updatedAt: string;
// }



interface CheckoutSuccessPayload {
  sessionId: string;
}

interface Admin { _id: string; }

interface GlobalContextType {
  // Analysis
  getAnalyticsData: () => void;
  getDailySalesData: () => void;
  analyticsData: IAnalyticsData | null;
  dailySalesData: IDailySalesData[];
  
  // Coupon
  createCoupon: (coupon: ICoupon) => void;
  getCoupon: () => void;
  validateCoupon: (coupon: ICoupon) => void;
  applyCoupon: (code: string) => void;
  removeCoupon: () => Promise<void>;
  coupons: ICoupon[];

  // Orders
  getOrders: () => Promise<void>;
  getMyOrders: () => Promise<void>;
  approveOrder: (id: string) => Promise<void>;
  declineOrder: (id: string) => Promise<void>;
  updateOrderStatus: (id: string, status: string) => Promise<void>;
  orders: IOrder[];
  myOrders: IOrder[];
  
  // Cart
  getCartProducts: (_id: string) => void;
  addToCart: (cart: IProduct) => void;
  removeAllFromCart: () => void;
  updateQuantity: (id: string, quantity: number) => void;
  carts: IProduct[];
  
  // Payment
  createCheckoutSession: (payload: CheckoutSuccessPayload) => Promise<void>;
  checkoutSuccess: () => void;
  payments: IPayment[];
  
  // Product
  getProducts: () => void;
  getFeaturedProducts: () => void;
  createProduct: (product: ICreateProductBody) => void;
  deleteProduct: (id: string) => void;
  getRecommendedProducts: () => Promise<IProduct[]>;
  updateProduct: (updatedProduct: IUpdateProductBody) => Promise<void>;
  getProductsByCategory: (category: string) => void;
  toggleFeaturedProduct: (id: string) => void;
  products: IProduct[];


  // User
  user: User | null;
  loadingUser: boolean;
  checkingAuth: boolean;
  
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  admin: boolean;
  setAdmin: (user: Admin | null) => void;
  logoutAdmin: () => void;
  logoutUser: () => void;

  subtotal: number;
  total: number;

   // Users
   users: User[];
   getAllUsers: () => Promise<void>;
   updateOnlineStatus: (userId: string, isOnline: boolean) => Promise<void>;
}

const GlobalContext = React.createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [coupons, setCoupons] = useState<ICoupon[]>([]);
  const [carts, setCarts] = useState<IProduct[]>([]);
  const [payments, setPayments] = useState<IPayment[]>([]);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [dailySalesData, setDailySalesData] = useState<IDailySalesData[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(
    JSON.parse(localStorage.getItem("currentUser") || "null")
  );
  const [analyticsData, setAnalyticsData] = useState<IAnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [checkingAuth, setCheckingAuth] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(JSON.parse(localStorage.getItem("user") || "null"));
  const [loadingUser] = useState<boolean>(false);
  const [admin, setAdminState] = useState<boolean>(false);
  const [subtotal, setSubtotal] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [orders, setOrders] = useState<IOrder[]>([]);
  const [myOrders, setMyOrders] = useState<IOrder[]>([]);
  const [users, setUsers] = useState<User[]>([]);







  useEffect(() => {
    const calculatedSubtotal = carts.reduce((sum, item) => sum + item.price * item.quantity, 0);
    setSubtotal(calculatedSubtotal);
  }, [carts]);

  useEffect(() => {
    const calculatedTotal = subtotal - (subtotal * (coupons[0]?.discountPercentage || 0) / 100);
    setTotal(calculatedTotal);
  }, [subtotal, coupons]);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
        localStorage.removeItem("user");
      }
    }

    const storedCart = localStorage.getItem("cart");
    if (storedCart) {
      try {
        setCarts(JSON.parse(storedCart));
      } catch (error) {
        console.error("Error parsing cart data from localStorage:", error);
        localStorage.removeItem("cart");
      }
    }
  }, []);

  useEffect(() => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
      setAdminState(user.role === "admin");
    } else {
      localStorage.removeItem("user");
      setAdminState(false);
    }
  }, [user]);

  const logoutUser = () => {
    setCurrentUser(null);
    localStorage.removeItem("currentUser");
  };

  // const getProductsByCategory = async (category: string): Promise<void> => {
  //   try {
  //     const response = await axiosInstance.get(`/products/category/${category}`);
  //     if (response.data.products && Array.isArray(response.data.products)) {
  //       setProducts(response.data.products);
  //     } else {
  //       console.error("Error: API did not return an array.", response.data);
  //     }
  //   } catch (error: any) {
  //     console.error("Error fetching products:", error.response?.data?.message || error.message);
  //   }
  // };

  const getProductsByCategory = async (category: string): Promise<void> => {
    try {
      const encodedCategory = encodeURIComponent(category); // Encode the category name
      const response = await axiosInstance.get(`/products/category/${encodedCategory}`);
      if (response.data.products && Array.isArray(response.data.products)) {
        if (response.data.products.length === 0) {
          console.warn(`No products found for category: ${category}`);
        }
        setProducts(response.data.products);
      } else {
        console.error("Error: API did not return an array.", response.data);
      }
    } catch (error: any) {
      console.error("Error fetching products:", error.response?.data?.message || error.message);
    }
  };
  
  const getCartProducts = async (userId: string): Promise<IProduct[]> => {
    try {
      const response = await axiosInstance.get(`/cart/${userId}`);
      console.log("Cart API Response:", response.data); // Log the response for debugging
        // Get the products (or an empty array if not in correct format)
      const products = Array.isArray(response.data.items) ? response.data.items : [];
      // Get the totalAmount (assuming it's a number, not an array)
      // const totalAmount = typeof response.data.totalAmount === 'number' ? response.data.totalAmount : 0;


    
      // Log the products that are being returned
      console.log("Returned Products:", products);
      // console.log("Total Amount:", totalAmount);
    
    // Return an object with both products and totalAmount
    return products;

    } catch (error: any) {
      console.error("Error fetching cart products:", error.response?.data?.message || error.message);
      return [];
    }
  };
  
  const fetchData = async (url: string, setter: Function, errorMessage: string) => {
    try {
      const response = await axiosInstance.get(url);
      setter(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      console.error(errorMessage, error.response?.data?.message || error.message);
    }
  };

  const createProduct = async (productData: ICreateProductBody) => {
    try {
      await axiosInstance.post("/products/addpro", productData);
      getProducts();
    } catch (error) {
      console.error("Error creating product:", error);
      setProducts([]);
    }
  };

  const updateProduct = async (updatedProduct: IUpdateProductBody) => {
    try {
      await axiosInstance.put(`/products/${updatedProduct._id}`, updatedProduct);
      getProducts();
    } catch (error) {
      console.error("Error updating product:", error);
    }
  };

  const toggleFeaturedProduct = async (productId: string) => {
    try {
      await axiosInstance.patch(`/products/featured_change/${productId}`, {});
      getProducts();
    } catch (error) {
      console.error("Error toggling featured product:", error);
      toast.error("Failed to toggle featured product");
    }
  };

  const getProducts = async () => {
    try {
      const response = await axiosInstance.get("/products");
      setProducts(response.data.products);
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
    }
  };




  const getRecommendedProducts = async (): Promise<IProduct[]> => {
    try {
      const response = await axiosInstance.get("/products/recommendations");
      return response.data.products;
    } catch (error) {
      console.error("Error fetching recommended products:", error);
      return [];
    }
  };

  const getAnalyticsData = async () => {
    try {
      const response = await axiosInstance.get("/analysis");
      setAnalyticsData(response.data.analyticsData);
    } catch (error) {
      console.error("Error fetching analytics:", error);
    }
  };

  const getDailySalesData = async () => {
    try {
      const response = await axiosInstance.get("/analysis");
      setDailySalesData(response.data.dailySalesData);
    } catch (error) {
      console.error("Error fetching daily sales data:", error);
    }
  };

  const createCheckoutSession = async (payload: CheckoutSuccessPayload) => {
    try {
      await axiosInstance.post("/payments/checkout-success", payload);
    } catch (error) {
      console.error("Error creating checkout session:", error);
      throw error;
    }
  };

  const getCoupon = () => fetchData(`/coupon/${currentUser?._id}`, setCoupons, "Error fetching coupons");
  const getFeaturedProducts = () => fetchData("/products/featured", setProducts, "Error fetching featured products");

  const postData = async (url: string, data: object, callback: (...args: any[]) => void, errorMessage: string, ...callbackArgs: any[]) => {
    try {
      await axiosInstance.post(url, data);
      callback(...callbackArgs);
    } catch (error: any) {
      console.error(errorMessage, error.response?.data?.message || error.message);
    }
  };

  const createCoupon = (coupon: ICoupon) => postData("/coupon", { ...coupon }, getCoupon, "Error creating coupon");
  const validateCoupon = (coupon: ICoupon) => postData("/coupon/validate", { ...coupon, userId: currentUser?._id }, getCoupon, "Error validating coupon");
  const addToCart = (cart: IProduct) => postData("/cart", { ...cart, userId: currentUser?._id }, getCartProducts, "Error adding to cart", currentUser?._id);

  const deleteProduct = async (id: string) => {
    try {
      await axiosInstance.delete(`/product/${id}`);
      getProducts();
    } catch (error: any) {
      console.error("Error deleting product", error.response?.data?.message || error.message);
    }
  };

  const removeAllFromCart = async () => {
    try {
      await axiosInstance.delete(`/cart/${currentUser?._id}`);
      setCarts([]);
    } catch (error: any) {
      console.error("Error clearing cart", error.response?.data?.message || error.message);
    }
  };

  // 🧾 Fetch all orders (Admin only)
// 🧾 Fetch all orders (Admin only)
const getOrders = async (): Promise<void> => {
  try {
    const response = await axiosInstance.get("/orders");
    const ordersData = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data.orders)
      ? response.data.orders
      : [];

    setOrders(ordersData);
  } catch (error: any) {
    console.error("Error fetching orders:", error.response?.data?.message || error.message);
    
  }
};

// const getOrders = useCallback(async (): Promise<void> => {
//   try {
//     const response = await axiosInstance.get("/orders");

//     const ordersData = Array.isArray(response.data)
//       ? response.data
//       : Array.isArray(response.data.orders)
//       ? response.data.orders
//       : null;

//     if (ordersData !== null) {
//       setOrders(ordersData);
//     }

//   } catch (error: any) {
//     console.error(
//       "Error fetching orders:",
//       error.response?.data?.message || error.message
//     );
//   }
// }, []);



  // 👤 Fetch logged-in user's orders
  const getMyOrders = async (): Promise<void> => {
    try {
      const response = await axiosInstance.get("/orders/my");
      if (Array.isArray(response.data)) {
        setMyOrders(response.data);
      } else {
        console.warn("Unexpected myOrders response:", response.data);
        setMyOrders([]);
      }
    } catch (error: any) {
      console.error("Error fetching my orders:", error.response?.data?.message || error.message);
      setMyOrders([]);
    }
  };

  // ✅ Approve order payment (Admin)
  const approveOrder = async (id: string): Promise<void> => {
    try {
      const response = await axiosInstance.patch(`/orders/${id}/approve`);
      console.log("Order approved:", response.data);
      getOrders(); // refresh list
    } catch (error: any) {
      console.error("Error approving order:", error.response?.data?.message || error.message);
    }
  };

  // ❌ Decline order payment (Admin)
  const declineOrder = async (id: string): Promise<void> => {
    try {
      const response = await axiosInstance.patch(`/orders/${id}/decline`);
      console.log("Order declined:", response.data);
      getOrders(); // refresh list
    } catch (error: any) {
      console.error("Error declining order:", error.response?.data?.message || error.message);
    }
  };


  const updateOrderStatus = async (

    id: string,
    status: string
  ): Promise<void> => {
    try {
      const response = await axiosInstance.put(
        `/orders/${id}/status`,
        { status }
      );

      console.log("Order status updated:", response.data);

      getOrders(); // refresh list
    } catch (error: any) {
      console.error(
        "Error updating order status:",
        error.response?.data?.message || error.message
      );
    }
  };

      



  const updateQuantity = async (id: string, quantity: number) => {
    try {
      await axiosInstance.put(`/cart/update/${id}`, { quantity });
      getCartProducts(currentUser?._id || "");
    } catch (error: any) {
      console.error("Error updating quantity", error.response?.data?.message || error.message);
    }
  };

  const applyCoupon = async (code: string) => {
    try {
      const response = await axiosInstance.post("/coupon/apply", { code, userId: currentUser?._id });
      setCoupons(response.data.coupons);
      toast.success("Coupon applied successfully!");
    } catch (error) {
      console.error("Error applying coupon:", error);
      toast.error("Failed to apply coupon");
    }
  };

  const removeCoupon = async () => {
    try {
      const response = await axiosInstance.delete("/coupon/remove", {
        data: { userId: currentUser?._id },
      });
      setCoupons(response.data.coupons);
      toast.success("Coupon removed successfully!");
    } catch (error) {
      console.error("Error removing coupon:", error);
      toast.error("Failed to remove coupon");
    }
  };

  const logout = async () => {
    try {
      await axiosInstance.post("/auth/logout");
      setUser(null);
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
    } catch (error) {
      toast.error("An error occurred during logout");
    }
  };

  const checkAuth = async () => {
    setCheckingAuth(true);
    try {
      const response = await axiosInstance.get("/profile");
      setUser(response.data);
    } catch (error) {
      console.error("Error checking auth:", error);
      setUser(null);
    } finally {
      setCheckingAuth(false);
    }
  };

  // in globalContext (or wherever you keep API helpers)

const getAllUsers = async (): Promise<void> => {
  try {
    // try the canonical endpoint (adjust if your API base path differs)
    const res = await axiosInstance.get("/auth/");
    // defensive parsing: support both res.data (array) or res.data.users
    const data = Array.isArray(res.data)
      ? res.data
      : Array.isArray(res.data.users)
      ? res.data.users
      : Array.isArray(res.data.data)
      ? res.data.data
      : [];

    if (!Array.isArray(data)) {
      console.warn("getAllUsers: unexpected shape", res.data);
      setUsers([]);
      return;
    }

    setUsers(data);
  } catch (err: any) {
    console.error("getAllUsers error:", err?.response?.data ?? err.message ?? err);
    setUsers([]);
  }
};

const updateOnlineStatus = async (userId: string, isOnline: boolean): Promise<void> => {
  try {
    // Try the most likely route first
    await axiosInstance.patch(`/auth/${userId}/online`, { isOnline });

    // Optimistically update local state
    setUsers(prev => prev.map(u => (u._id === userId ? { ...u, isOnline, lastActive: new Date().toISOString() } : u)));
  } catch (err: any) {
    console.error("updateOnlineStatus error:", err?.response?.data ?? err.message ?? err);
    // fallback: maybe your backend uses a different patch path; try alternative
    try {
      await axiosInstance.patch(`/users/${userId}/online`, { isOnline }); // fallback
      setUsers(prev => prev.map(u => (u._id === userId ? { ...u, isOnline, lastActive: new Date().toISOString() } : u)));
    } catch (err2) {
      console.error("updateOnlineStatus fallback also failed:", err2);
    }
  }
};


  const setAdmin = (user: Admin | null) => {
    setAdminState(user !== null);
    localStorage.setItem("admin", JSON.stringify(user));
  };

  const logoutAdmin = () => {
    setAdmin(null);
    localStorage.removeItem("admin");
  };

  return (
    <GlobalContext.Provider
      value={{
        getAnalyticsData, getDailySalesData, analyticsData, dailySalesData,
        createCoupon, getCoupon, validateCoupon, coupons,
        applyCoupon, removeCoupon,
        getCartProducts, addToCart, removeAllFromCart, updateQuantity, carts,
        user, loadingUser, checkAuth, logout,updateProduct,
        getProducts, getFeaturedProducts, createProduct, deleteProduct, getRecommendedProducts, getProductsByCategory, toggleFeaturedProduct, products,
        payments, createCheckoutSession, checkoutSuccess, checkingAuth,
        currentUser, setCurrentUser, admin, setAdmin, logoutAdmin, logoutUser,
        orders, myOrders, getOrders, getMyOrders, approveOrder, declineOrder,
        total, subtotal, users,
        getAllUsers,
        updateOnlineStatus, updateOrderStatus,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) throw new Error("useGlobalContext must be used within GlobalProvider");
  return context;
};

function checkoutSuccess(): void {
  throw new Error("Function not implemented.");
}

function useCallback(arg0: () => Promise<void>, arg1: never[]) {
  throw new Error("Function not implemented.");
}
